// Approval Matrix — Super Admin only
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, GitBranch } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../utils/api';

const ROLE_OPTIONS = [
  { value: 'procurement_team',    label: 'Procurement Team' },
  { value: 'director_procurement',label: 'Director Procurement' },
  { value: 'finance_team',        label: 'Finance Team' },
  { value: 'finance_manager',     label: 'Finance Manager' },
  { value: 'super_admin',         label: 'Super Admin' },
];

const ApprovalMatrix = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRule, setNewRule] = useState({ name: '', department: '', required_roles: ['finance_team', 'finance_manager'], threshold_type: 'any', priority: 100, active: true });

  const fetchRules = async () => {
    try {
      const r = await axios.get(`${API}/approval-matrix`);
      setRules(r.data);
    } catch (err) { toast.error('Failed to load rules'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleCreate = async () => {
    if (!newRule.name || newRule.required_roles.length === 0) {
      toast.error('Name + at least one required role');
      return;
    }
    try {
      // Amount is not filtered by the resolver; send 0/null so old rules stay compatible.
      await axios.post(`${API}/approval-matrix`, { ...newRule, amount_min: 0, amount_max: null });
      toast.success('Rule created');
      setNewRule({ name: '', department: '', required_roles: ['finance_team', 'finance_manager'], threshold_type: 'any', priority: 100, active: true });
      fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await axios.delete(`${API}/approval-matrix/${id}`); toast.success('Deleted'); fetchRules(); }
    catch (err) { toast.error('Failed'); }
  };

  const toggleRole = (role) => {
    setNewRule(p => ({ ...p, required_roles: p.required_roles.includes(role) ? p.required_roles.filter(r => r !== role) : [...p.required_roles, role] }));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="animate-fadeIn" data-testid="approval-matrix-page">
      <div className="page-header">
        <h1 className="page-title"><GitBranch className="inline w-6 h-6 mr-2" /> Approval Matrix</h1>
        <p className="page-subtitle">Every Work Order routes to Finance. Rules add extra approvers or change roles by department.</p>
      </div>

      <div className="form-card" style={{ marginBottom: 16, padding: 14, background: 'var(--gold-pale)', border: '1px solid var(--gold-light)' }}>
        <div style={{ fontSize: 13, color: 'var(--heading)', fontWeight: 500 }}>
          ✓ Default flow (no rules needed): every submitted Work Order goes to <strong>Finance Team + Finance Manager + Super Admin</strong>.
        </div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          Add rules below only if a specific department needs different approvers. Amount thresholds are ignored — Finance approves every WO.
        </div>
      </div>

      <div className="form-card" style={{ marginBottom: 24 }}>
        <div style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Add New Rule</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <div><Label>Rule Name</Label><Input value={newRule.name} onChange={e => setNewRule({ ...newRule, name: e.target.value })} placeholder="e.g. IT dept — extra approver" data-testid="rule-name" /></div>
            <div><Label>Dept (optional)</Label><Input value={newRule.department} onChange={e => setNewRule({ ...newRule, department: e.target.value })} placeholder="IT / F&B / any" data-testid="rule-dept" /></div>
            <div><Label>Priority</Label><Input type="number" value={newRule.priority} onChange={e => setNewRule({ ...newRule, priority: parseInt(e.target.value) || 100 })} data-testid="rule-priority" /></div>
            <Button onClick={handleCreate} data-testid="create-rule-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
          <div style={{ marginTop: 12 }}>
            <Label>Required Approver Roles (click to toggle) — Finance roles pre-selected</Label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {ROLE_OPTIONS.map(r => (
                <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                        style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid #ddd',
                                 background: newRule.required_roles.includes(r.value) ? '#0f2a5a' : 'white',
                                 color: newRule.required_roles.includes(r.value) ? 'white' : '#333',
                                 cursor: 'pointer', fontSize: 12 }}
                        data-testid={`role-toggle-${r.value}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="form-card">
        <div style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Active Rules ({rules.length})</div>
          {rules.length === 0 ? (
            <p style={{ color: '#888', fontSize: 13 }}>No rules yet — default Finance-loop flow is active for every Work Order.</p>
          ) : (
            <table className="contract-table" style={{ width: '100%' }}>
              <thead><tr><th>Priority</th><th>Name</th><th>Dept</th><th>Roles</th><th></th></tr></thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.id}>
                    <td>{r.priority}</td>
                    <td>{r.name}</td>
                    <td>{r.department || 'any'}</td>
                    <td style={{ fontSize: 11 }}>{r.required_roles.join(', ')}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalMatrix;
